/**
 * Réglages du planning v2 lus dans window.mondayMindbody (injecté par Frontend.php).
 *
 * `uxV2.multiBrand`, `uxV2.brands` et `uxV2.reservationUrl` (SPEC § 5.2, § 6.1) sont ajoutés par le serveur quand
 * la v2 est active ; tant qu'ils manquent, le planning reste celui du site seul, sans filtre Marque ni légende.
 */
import { getCourseTypeOptions } from 'screens/Classes/courseTypeFilters';
import type { RawBrand } from '../../lib/planning/brands';
import { eventSplitOf, type EventSplit } from '../../lib/planning/eventStudios';

/** Champs de `mondayMindbody.uxV2` propres au planning (SPEC § 5.2, § 6.1). */
interface UxV2PlanningFields {
  multiBrand?: boolean;
  brands?: RawBrand[];
  reservationUrl?: string;
  groupText?: string;
  planningTitle?: string;
  specialEventsLabel?: string;
}

export interface PlanningConfig {
  currentSiteId: string;
  /** Ville à plusieurs marques (Helper::getRegionalSiteGroups) : planning de toutes les marques de la ville. */
  multiBrand: boolean;
  rawBrands: RawBrand[];
  /** Page de réservation du site courant ('' = « /reservation/ », comme la v1). */
  reservationUrl: string;
  /** Shortcode `[mindbody coach="…"]` : coach figé (= Filters/Coaches.tsx shortCodeCoachId). */
  coachId: number | null;
  /**
   * Shortcode `[mindbody cours="…"]` (pages de cours des thèmes) : type de cours figé, valeur du filtre « Cours » du
   * site (courseTypeFilters.ts) ; null sans attribut ou pour un type inconnu du site (planning complet).
   */
  courseType: string | null;
  /** Réglage `days` (fenêtre de réservation). */
  numberOfDays: number;
  /** Toggle BO « Activer le filtre type de cours ». */
  courseTypeFilterEnabled: boolean;
  /** Réglage « Nom de la marque ». */
  brandName: string;
  /** Réglage « Encart groupe du planning » (UxV2::getClientConfig) : '' = pas d'encart. */
  groupText: string;
  /** Réglage « Titre du planning » (UxV2::planningTitle) : h1 de la page de réservation, « Planning » par défaut. */
  planningTitle: string;
  /**
   * Réglage « Nom de l'entrée Special Events du studio Monday Event » (UxV2::specialEventsLabel) : studios virtuels
   * « Run Club » et Special Events du studio Monday Event de Paris (lib/planning/eventStudios.ts) ; null s'il est vide.
   */
  eventSplit: EventSplit | null;
}

export function readPlanningConfig(): PlanningConfig {
  const mm = typeof mondayMindbody !== 'undefined' && mondayMindbody ? mondayMindbody : undefined;
  const ux = (mm?.uxV2 ?? {}) as UxV2PlanningFields;
  const coach = mm?.coach?.length ? parseInt(mm.coach, 10) : NaN;
  const siteId = String(mm?.siteId ?? '');
  const courseType = typeof mm?.courseType === 'string' ? mm.courseType.trim() : '';
  const days = Number(mm?.customization?.numberOfDays);
  return {
    currentSiteId: siteId,
    multiBrand: ux.multiBrand === true,
    rawBrands: Array.isArray(ux.brands) ? ux.brands : [],
    reservationUrl: typeof ux.reservationUrl === 'string' ? ux.reservationUrl.trim() : '',
    coachId: Number.isFinite(coach) && coach > 0 ? coach : null,
    courseType: getCourseTypeOptions(siteId).some((o) => o.value === courseType) ? courseType : null,
    // = utils/functions.ts : 8 jours sans réglage.
    numberOfDays: Number.isFinite(days) && days > 0 ? days : 8,
    courseTypeFilterEnabled: Boolean(mm?.customization?.enableCourseTypeFilter),
    brandName: String(mm?.customization?.brandName ?? ''),
    groupText: typeof ux.groupText === 'string' ? ux.groupText.replace(/\s+/g, ' ').trim() : '',
    planningTitle:
      (typeof ux.planningTitle === 'string' ? ux.planningTitle.replace(/\s+/g, ' ').trim() : '') || 'Planning',
    eventSplit: eventSplitOf(ux.specialEventsLabel),
  };
}

/**
 * Titre du planning découpé aux premiers deux-points : « Planning : réserver un cours RIISE » donne le titre
 * « Planning » et le sous-titre « réserver un cours RIISE », affiché en dessous en gris, en casse normale, dans le
 * même h1 (demande de Daphné du 27/10). Sans deux-points, ou un côté vide : tout le texte en titre. = UxV2::planningTitleParts.
 */
export function splitPlanningTitle(title: string): { main: string; sub: string } {
  const at = title.indexOf(':');
  const main = at < 0 ? '' : title.slice(0, at).trim();
  const sub = at < 0 ? '' : title.slice(at + 1).trim();
  return main && sub ? { main, sub } : { main: title, sub: '' };
}
